
import {
  computed,
  defineComponent, onMounted, ref, watch,
} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { Form, defineRule } from 'vee-validate';
import {
  IConversation,
  IFileUpload,
  IImageCrop, INotificationRead, IPhoto, IRequestSpacesGroup, ISpacesContact,
} from '@/types/interfaces';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import { useMessagesStore } from '../store/messagesStore';
import { useMainStore } from '@/store/mainStore';
import { useSpacesStore } from '@/modules/spaces/store/spacesStore';
import { lowResUrlByViewTypeByUrl } from '@/helpers/lowResPhotoUrl';
import {
  GROUP_TYPE,
  GROUP_USER_STATUS,
  MESSAGE_SHARED_ITEM_TYPES,
  NETWORK_CONTACT_TYPES,
  NOTIFICATION_READ_MODE,
  NOTIFICATION_TYPE,
  PHOTO_VIEW_TYPE,
  UPLOAD_PLACES,
} from '@/types/enums';
import SpacesService from '@/modules/spaces/services/SpacesService';
import ValidationService from '@/services/ValidationService';
import SpacesUser from '@/modules/spaces/components/SpacesUser.vue';
import AppHeader from '@/components/AppHeader.vue';
import AppTakePhoto from '@/components/AppTakePhoto.vue';
import AppInput from '@/components/UI/AppInput.vue';
import AppToggle from '@/components/UI/AppToggle.vue';
import MessageGroupAddPeople from './MessageGroupAddPeople.vue';
import MessageGroupFiles from './MessageGroupFiles.vue';
import MessageGroupLeaveConfirmation from './MessageGroupLeaveConfirmation.vue';
import MessageGroupDeleteConfirmation from './MessageGroupDeleteConfirmation.vue';
import AppLoader from '@/components/UI/AppLoader.vue';
import UseUpload from '@/composables/UseUpload';
import UploadService from '@/services/UploadService';
import AppUploadInfo from '@/components/UI/AppUploadInfo.vue';
import AppLazyImage from '@/components/UI/AppLazyImage.vue';
import UserService from '@/services/UserService';
import DesktopHeader from '@/components/desktop/DesktopHeader.vue';
import DriveAsideMainMenu from '@/modules/drive/components/desktop/DriveAsideMainMenu.vue';

defineRule('required', (value: string) => ValidationService.isNotEmpty(value) || 'This field is required');
defineRule('min5', (value: string) => ValidationService.isLonger(value, 5) || 'Group name must be at least 5 characters long.');

export default defineComponent({
  emits: ['close'],
  components: {
    DriveAsideMainMenu,
    DesktopHeader,
    AppHeader,
    AppInput,
    AppToggle,
    AppTakePhoto,
    AppLoader,
    SpacesUser,
    MessageGroupAddPeople,
    MessageGroupFiles,
    MessageGroupLeaveConfirmation,
    MessageGroupDeleteConfirmation,
    AppUploadInfo,
    AppLazyImage,
    Form,
  },
  setup(props, { emit }) {
    const messagesStore = useMessagesStore();
    const driveStore = useDriveStore();
    const mainStore = useMainStore();
    const spacesStore = useSpacesStore();
    const router = useRouter();
    const route = useRoute();
    const backStep = ref<undefined | string>(undefined);

    const editNameMode = ref(false);
    const addContacts = ref(false);
    const groupLeaveConfirm = ref(false);
    const groupDeleteConfirm = ref(false);
    const isViewAllMembers = ref(false);
    const isViewAllRequestsToGroup = ref(false);

    const contacts = ref<ISpacesContact[]>([]);
    const dataLoading = ref(false);

    const showSharedItems = ref<MESSAGE_SHARED_ITEM_TYPES | null>(null);

    const schema = {
      groupName: 'required|min5',
    };

    const isValid = ref(true);

    const members = computed(() => {
      if (isViewAllMembers.value) {
        return messagesStore.currentConversation?.group?.members;
      }
      return messagesStore.currentConversation?.group?.members.slice(0, 10) || [];
    });

    const requestsToGroup = computed<IRequestSpacesGroup[]>(() => messagesStore.currentConversation?.group?.requestsToGroup || []);

    const filteredRequestsToGroup = computed(() => {
      if (isViewAllRequestsToGroup.value) {
        return requestsToGroup.value;
      }
      return requestsToGroup.value.slice(0, 10) || [];
    });

    const selectedContacts = computed<ISpacesContact[]>(() => contacts.value.filter((cont) => cont.selected));

    const isGroupOwner = computed(() => messagesStore.currentConversation?.group?.ownerId === mainStore.user.accountId);
    const isGroupAdmin = computed(() => messagesStore.currentConversation?.group?.members.find((member) => member.accountId === mainStore.user.accountId)?.type === GROUP_USER_STATUS.ADMIN || isGroupOwner.value);
    const isGroupMember = computed(() => messagesStore.currentConversation?.group?.members.find((member) => member.accountId === mainStore.user.accountId));

    const photoUrl = computed(() => {
      if (messagesStore.currentConversation?.info.photo) {
        return lowResUrlByViewTypeByUrl(messagesStore.currentConversation.info.photo.url, PHOTO_VIEW_TYPE.WHSML);
      }
      // eslint-disable-next-line global-require
      return require('@/assets/img/mockup/Group.svg');
    });

    const fileInput = ref<HTMLInputElement | null>(null);
    const fileInputKey = ref(0);
    const imageToCrop = ref({} as IImageCrop);

    const { uploadInfo, onUploadUpdate } = UseUpload();

    const loaderText = computed(
      () => '',
    );

    function savePhoto(event: Event) {
      const img: File = (event.target as HTMLInputElement).files![0];

      imageToCrop.value = {
        url: URL.createObjectURL(img),
        fileName: img.name,
        fileType: img.type,
      };
      fileInputKey.value += 1;

      fileInput.value!.value = '';
    }

    async function updateGroupInfo(currentConversation: IConversation, onlyName?: boolean) {
      if (!isValid.value) {
        messagesStore.currentConversation!.info.name = messagesStore.currentConversation!.group!.name;
        isValid.value = true;
      }

      const playload = {
        id: currentConversation.info.id,
        name: currentConversation.info.name,
        photo: onlyName ? null : currentConversation.info.photo,
        groupType: currentConversation.group?.groupType,
      };

      return SpacesService.updateGroup(playload).then((response) => {
        if (response.success) {
          // NotificationService.notifySuccess();
        }
        return response;
      });
    }

    async function fetchGroupData() {
      if (!messagesStore.currentConversation?.group?.id) {
        return;
      }
      SpacesService.getGroupById(messagesStore.currentConversation.group.id).then((response) => {
        if (response.success) {
          messagesStore.currentConversation!.group = response.results.data;
        }
        // TODO add error handling (404)
      });
    }

    async function handleCroppedImage(fileUpload: {file: IFileUpload[]}) {
      const { currentConversation } = messagesStore;
      imageToCrop.value = {} as IImageCrop;
      // dataLoading.value = true;

      UploadService.batchUploadTUS(fileUpload.file, onUploadUpdate, { location: UPLOAD_PLACES.PROFILE_PHOTO }, undefined, [], true, true)
        .then((response) => {
          const res = response[0];

          const photo: IPhoto = {
            id: res.id,
            url: res.fileUrl,
          };

          if (currentConversation) {
            currentConversation.info.photo = photo;
            updateGroupInfo(currentConversation);
          }
        });
      // .finally(() => { dataLoading.value = false; });
    }

    function onEditMode() {
      if (!isValid.value) {
        return;
      }
      if (editNameMode.value && messagesStore.currentConversation) {
        updateGroupInfo(messagesStore.currentConversation, true);
      }
      editNameMode.value = !editNameMode.value;
    }

    function leaveGroup(status: string) {
      const payload = {
        groupId: messagesStore.currentConversation!.info.id,
        status,
      };

      SpacesService.leaveGroup(payload).then((response) => {
        if (response.success) {
          spacesStore.myGroups = spacesStore.myGroups.filter((group) => group.id !== payload.groupId);
          emit('close');
          router.push('/messages/my');
        }
      });
    }

    function deleteGroup() {
      SpacesService.deleteGroup(messagesStore.currentConversation!.info.id).then((response) => {
        if (response.success) {
          // NotificationService.notifySuccess();
          emit('close');
          if (mainStore.isMobile) {
            router.push('/spaces/groups');
          } else {
            messagesStore.fetchConversations();
            router.push('/messages/my');
          }
        }
      });
    }

    function validate() {
      isValid.value = ValidationService.isNotEmpty(messagesStore.currentConversation!.info.name)
        && ValidationService.isLonger(messagesStore.currentConversation!.info.name, 5);
    }

    function cancelUpload() {
      UploadService.isAbortUpload = true;
      dataLoading.value = false;
    }

    function handleBack() {
      if (backStep.value) {
        emit('close');
        router.push(backStep.value);
      } else {
        emit('close');
      }
    }

    function openGroupLeaveConfirm() {
      if (isGroupOwner.value) {
        return;
      }
      groupLeaveConfirm.value = true;
    }
    function openGroupDeleteConfirm() {
      if (!isGroupOwner.value) {
        return;
      }
      groupDeleteConfirm.value = true;
    }

    function viewAllMembers() {
      isViewAllMembers.value = !isViewAllMembers.value;
    }

    function makeGroupPublic() {
      if (!messagesStore.currentConversation || !messagesStore.currentConversation.group) {
        return;
      }

      const { group } = messagesStore.currentConversation;

      switch (group.groupType) {
        case GROUP_TYPE.PUBLIC_OPEN:
          group.groupType = GROUP_TYPE.PRIVATE;
          break;
        case GROUP_TYPE.PRIVATE:
          group.groupType = GROUP_TYPE.PUBLIC_OPEN;
          break;
        default:
          group.groupType = GROUP_TYPE.PUBLIC_OPEN;
          break;
      }

      updateGroupInfo(messagesStore.currentConversation);
    }

    function acceptUserToGroup(userId: string) {
      const currentUser = requestsToGroup.value.find((request) => request.partner?.accountId === userId)?.partner;
      if (messagesStore.currentConversation?.group?.members && currentUser) {
        messagesStore.currentConversation.group.requestsToGroup = requestsToGroup.value.filter((request) => request.partner?.accountId !== userId);
        messagesStore.currentConversation.group.members.push(currentUser as unknown as ISpacesContact);
      }
    }

    function rejectUserToGroup(userId: string) {
      if (messagesStore.currentConversation?.group?.requestsToGroup) {
        messagesStore.currentConversation.group.requestsToGroup = requestsToGroup.value.filter((request) => request.partner?.accountId !== userId);
      }
    }

    function readNotification(groupId: string, badgesCount: number) {
      if (groupId) {
        const payload: INotificationRead[] = [{
          contextId: groupId,
          type: NOTIFICATION_TYPE.IN_GROUP_REQUEST_SENT,
          mode: NOTIFICATION_READ_MODE.MIXED,
        }];
        UserService.readNotification(payload);
        mainStore.badgesInGroup -= badgesCount;
      }
    }

    watch(() => messagesStore.currentConversation, (newValue) => {
      if (!newValue) {
        emit('close');
      }
    });

    onMounted(() => {
      if (route.query.openGroupInfo) {
        backStep.value = '/messages/my';
      } else {
        backStep.value = undefined;
      }
      SpacesService.fetchMyContacts().then((response) => {
        if (response.success) {
          contacts.value = response.results.data.map((cont) => cont.user);
          router.replace({});
        }
      });
      if (messagesStore.currentConversation?.info.id && isGroupAdmin.value) {
        SpacesService.fetchRequestsToGroup(messagesStore.currentConversation.info.id).then((response) => {
          if (response.success && messagesStore.currentConversation?.group) {
            readNotification(messagesStore.currentConversation.info.id, response.results.data.length);
            messagesStore.currentConversation.group.requestsToGroup = response.results.data;
          }
        });
      }
    });

    return {
      driveStore,
      mainStore,
      photoUrl,
      fileInputKey,
      fileInput,
      imageToCrop,
      contacts,
      requestsToGroup,
      filteredRequestsToGroup,
      selectedContacts,
      messagesStore,
      editNameMode,
      isGroupOwner,
      isGroupAdmin,
      isGroupMember,
      addContacts,
      isValid,
      schema,
      showSharedItems,
      dataLoading,
      loaderText,
      uploadInfo,
      groupLeaveConfirm,
      groupDeleteConfirm,
      validate,
      savePhoto,
      handleCroppedImage,
      onEditMode,
      leaveGroup,
      deleteGroup,
      cancelUpload,
      handleBack,
      openGroupLeaveConfirm,
      openGroupDeleteConfirm,
      makeGroupPublic,
      acceptUserToGroup,
      rejectUserToGroup,
      MESSAGE_SHARED_ITEM_TYPES,
      NETWORK_CONTACT_TYPES,
      GROUP_TYPE,
      fetchGroupData,
      isViewAllMembers,
      isViewAllRequestsToGroup,
      viewAllMembers,
      members,
    };
  },
});
